import { useSecurityGroupNames } from '@internal/plugin-azure-resources-common-react';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import React from 'react';

import { parseEntityRef } from '@backstage/catalog-model';
import { InfoCardVariants } from '@backstage/core-components';
import { EntityRefLinks, useEntity } from '@backstage/plugin-catalog-react';

import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import { KubernetesBlueLogo } from '../../../home/KubernetesLogo';
import { Badge } from './DaasBadge';
import { ContentSection } from './DaasContentSection';
import { InlineField } from './DaasInlineField';
import { DaasSectionItem } from './DaasSectionItem';
import { useStyles } from './styles';
import { ClusterConfig } from './types';

interface ClusterItemProps {
  cluster: ClusterConfig;
}

const tryParseEntityRef = (ref?: string) => {
  if (!ref) return undefined;
  try {
    return parseEntityRef(ref);
  } catch {
    return undefined;
  }
};

const ClusterItem = ({ cluster }: ClusterItemProps) => {
  const parsedRef = tryParseEntityRef(cluster.catalogRef);
  const title = parsedRef ? (
    <EntityRefLinks
      entityRefs={[parsedRef]}
      defaultKind={parsedRef.kind}
      hideIcons
    />
  ) : (
    cluster.name
  );

  return (
    <DaasSectionItem icon={<KubernetesBlueLogo />} title={title}>
      <InlineField label="Server" copyText={cluster.server}>
        <Typography variant="body1">{cluster.server}</Typography>
      </InlineField>
      {cluster.id && (
        <InlineField label="ID">
          <Typography variant="body1">{cluster.id}</Typography>
        </InlineField>
      )}
      {cluster.kaapTenant && (
        <InlineField label="KaaP Tenant" copyText={cluster.kaapTenant.name}>
          <Typography variant="body2">{cluster.kaapTenant.name}</Typography>
        </InlineField>
      )}
      {cluster.namespaces && (
        <InlineField label="Namespaces">
          <Box display="flex" flexWrap="wrap" gridGap={4}>
            {cluster.namespaces.map(ns => (
              <Badge key={ns} type="namespace" label={ns} />
            ))}
          </Box>
        </InlineField>
      )}
    </DaasSectionItem>
  );
};

export interface DaasProjectContentCardProps {
  variant?: InfoCardVariants;
}

export const DaasProjectContentCard = ({
  variant,
}: DaasProjectContentCardProps) => {
  const { entity } = useEntity();
  const classes = useStyles();

  const cardClass =
    variant === 'gridItem' ? classes.gridItemCard : classes.fullHeightCard;
  const cardContentClass =
    variant === 'gridItem'
      ? classes.gridItemCardContent
      : classes.fullHeightCardContent;

  const notificationChannel =
    (entity.spec?.notificationChannel as string) || null;
  const notificationServices =
    (entity.spec?.notificationServices as string[]) || [];
  const securityGroups =
    (entity.spec?.securityGroups as string[] | undefined) ?? [];
  // Projects created before the GitHub-only field was generalised still carry
  // spec.githubActionsSubjects.
  const cicdOidcSubjects =
    (entity.spec?.cicdOidcSubjects as string[] | undefined) ??
    (entity.spec?.githubActionsSubjects as string[] | undefined) ??
    [];
  const { groups: securityGroupNames, loading: securityGroupsLoading } =
    useSecurityGroupNames(securityGroups);
  const clusters = (entity.spec?.clusters as unknown as ClusterConfig[]) || [];

  return (
    <Card className={cardClass}>
      <CardHeader title="Project Details" className={classes.cardHeader} />
      <Divider />
      <CardContent className={cardContentClass}>
        <Grid container spacing={3}>
          <Grid item md={6} xs={12}>
            <ContentSection label="Clusters" gridSizes={{ xs: 12 }}>
              {clusters.length > 0 ? (
                clusters.map((cluster, index) => (
                  <React.Fragment
                    key={cluster.catalogRef ?? cluster.server ?? index}
                  >
                    <ClusterItem cluster={cluster} />
                  </React.Fragment>
                ))
              ) : (
                <Typography variant="body1">No clusters configured</Typography>
              )}
            </ContentSection>
          </Grid>
          <Grid item md={6} xs={12}>
            <ContentSection label="Security Groups" gridSizes={{ xs: 12 }}>
              {securityGroups.length > 0 ? (
                securityGroups.map(sg => (
                  <React.Fragment key={sg}>
                    <DaasSectionItem
                      icon={<ShieldOutlinedIcon />}
                      title={
                        securityGroupsLoading
                          ? 'Loading...'
                          : securityGroupNames.get(sg) || sg
                      }
                      subTitle={securityGroupNames.get(sg) ? sg : undefined}
                    />
                  </React.Fragment>
                ))
              ) : (
                <Typography variant="body1">No security groups</Typography>
              )}
            </ContentSection>
          </Grid>
          <Grid item md={6} xs={12}>
            <ContentSection label="Notifications" gridSizes={{ xs: 12 }}>
              <DaasSectionItem>
                <InlineField label="Channel">
                  {notificationChannel ? (
                    <Typography variant="body2">
                      #{notificationChannel}
                    </Typography>
                  ) : (
                    <Typography variant="body1">&mdash;</Typography>
                  )}
                </InlineField>
                <InlineField label="Services">
                  <Typography variant="body1">
                    <Box display="flex" flexWrap="wrap" gridGap={8}>
                      {notificationServices.length > 0 ? (
                        notificationServices.map(ns => (
                          <Badge key={ns} type={ns} label={ns} />
                        ))
                      ) : (
                        <>&mdash;</>
                      )}
                    </Box>
                  </Typography>
                </InlineField>
              </DaasSectionItem>
            </ContentSection>
          </Grid>
          <Grid item md={6} xs={12}>
            <ContentSection label="CI/CD OIDC Access" gridSizes={{ xs: 12 }}>
              {cicdOidcSubjects.length > 0 ? (
                cicdOidcSubjects.map(subject => (
                  <DaasSectionItem
                    key={subject}
                    icon={<KeyOutlinedIcon />}
                    title={subject}
                    truncate
                  />
                ))
              ) : (
                <Typography variant="body1">No CI/CD OIDC access</Typography>
              )}
            </ContentSection>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
